import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Activity, Loader2, CheckCircle2, AlertTriangle, HelpCircle, Waves, Flame, Tornado } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', green: '#1D9E75', red: '#E24B4A', amber: '#B27516', slate: '#5C6B7A', bg: '#F5F7FA' };

const VERDICT = {
  healthy: { color: C.green, Icon: CheckCircle2, label: 'Answering' },
  suspect: { color: C.red, Icon: AlertTriangle, label: 'Suspect' },
  unavailable: { color: C.amber, Icon: AlertTriangle, label: 'Unavailable' },
};

const TIERS = [
  { key: 'council', label: 'Council flood studies', note: 'most property-specific — decides first' },
  { key: 'state', label: 'State planning flood overlays', note: 'decides when no council layer covers the address' },
  { key: 'catchment', label: 'BOM flood catchments', note: 'decides last — capped at medium' },
  { key: 'storm_surge', label: 'Storm tide / cyclone surge', note: 'a separate inundation peril — prices, never refers; not assessed where no layer answers' },
  { key: 'bushfire', label: 'Bushfire prone land', note: 'one layer per state' },
];

const PERIL_ICON = { flood: [Waves, C.blue], bushfire: [Flame, C.amber], storm_surge: [Tornado, C.navy] };

const bandStyle = band => /high/.test(band) ? { background: 'rgba(226,75,74,0.1)', color: C.red }
  : /medium/.test(band) ? { background: 'rgba(178,117,22,0.1)', color: C.amber }
    : { background: C.bg, color: C.slate };

const SourceRow = ({ s, verdict }) => {
  const v = VERDICT[verdict?.verdict] || { color: C.slate, Icon: HelpCircle, label: 'Unprobed' };
  const [Peril, perilColor] = PERIL_ICON[s.peril] || PERIL_ICON.flood;
  return (
    <tr className="border-t border-[rgba(136,150,168,0.1)]" data-testid={`hazard-source-${s.code}`}>
      <td className="px-4 py-3 align-top whitespace-normal">
        <p className="text-[12px] font-medium flex items-start gap-1.5" style={{ color: C.navy }}>
          <Peril className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: perilColor }} />
          {s.name}
        </p>
        <p className="text-[10px] mt-0.5 pl-5" style={{ color: C.slate }}>{s.agency} · {s.layer}</p>
        {s.publishes && <p className="text-[10px] mt-1 pl-5 italic" style={{ color: C.navy }}>Publishes {s.publishes}</p>}
        {s.grades && (
          <p className="pl-5 mt-1 flex flex-wrap gap-1" data-testid={`hazard-grades-${s.code}`}>
            {Object.entries(s.grades).map(([k, band]) => (
              <span key={k} className="text-[9px] font-mono px-1 py-0.5 rounded" style={bandStyle(band)}>{k} → {band}</span>
            ))}
          </p>
        )}
      </td>
      <td className="px-4 py-3 align-top text-[11px] font-mono" style={{ color: C.navy }}>{s.jurisdiction}</td>
      <td className="px-4 py-3 align-top text-[10px] break-words whitespace-normal" style={{ color: C.slate }}>
        {s.partial && <span className="text-[9px] font-semibold px-1 py-0.5 rounded uppercase mr-1" style={bandStyle('medium')} data-testid={`hazard-partial-${s.code}`}>partial</span>}
        {s.coverage}
        <p className="mt-1 opacity-80">{s.licence}</p>
      </td>
      <td className="px-4 py-3 align-top">
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase inline-flex items-center gap-1"
          style={{ background: v.color + '15', color: v.color }}>
          <v.Icon className="w-3 h-3" />{v.label}
        </span>
      </td>
    </tr>
  );
};

export const HazardSourcesCard = ({ api }) => {
  const [data, setData] = useState(null);
  const [probing, setProbing] = useState(false);

  const load = async () => {
    try { const { data } = await api.get('/enrichment/hazard-sources'); setData(data); }
    catch (err) { console.error('API error:', err?.message); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const probe = async () => {
    setProbing(true);
    try {
      const { data: health } = await api.post('/enrichment/hazard-sources/probe');
      setData(d => ({ ...d, health }));
    } catch (err) { console.error('API error:', err?.message); }
    setProbing(false);
  };

  if (!data) return null;
  const health = data.health || {};
  const verdicts = Object.fromEntries((health.checks || []).map(c => [c.code, c]));
  const sources = data.sources || [];
  const tierOf = s => s.peril === 'flood' ? s.tier : s.peril;

  return (
    <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="hazard-sources-card"
      style={{ borderLeft: `3px solid ${C.green}` }}>
      <div className="p-5 pb-4 flex items-start justify-between flex-wrap gap-3">
        <div className="max-w-3xl">
          <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2" style={{ color: C.navy }}>
            <Activity className="w-4 h-4" style={{ color: C.green }} />Live hazard sources
            <span className="text-[10px] font-mono normal-case tracking-normal px-1.5 py-0.5 rounded" style={{ background: C.bg, color: C.slate }} data-testid="hazard-source-count">{sources.length} layers</span>
          </h3>
          <p className="text-xs mt-1" style={{ color: C.slate }}>
            Flood, bushfire and storm tide are read from the agency that publishes the determination, at the geocoded
            address. Each answer is cited with its layer, retrieval time and licence. A layer that stops
            answering would return &ldquo;no feature&rdquo; forever — which reads as <em>not in a hazard area</em> —
            so every layer is asked a question we already know the answer to, daily.
          </p>
        </div>
        <div className="text-right">
          <Button onClick={probe} disabled={probing} data-testid="hazard-probe-btn"
            className="rounded-sm text-xs" style={{ background: C.blue }}>
            {probing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Activity className="w-3.5 h-3.5 mr-1.5" />}
            Probe now
          </Button>
          <p className="text-[10px] mt-1.5" style={{ color: health.all_healthy ? C.green : C.slate }} data-testid="hazard-health-summary">
            {health.checked_at ? `${health.healthy}/${health.total} answering · ${health.checked_at.slice(0, 16).replace('T', ' ')}Z`
              : 'Not probed yet'}
          </p>
        </div>
      </div>

      {data.precedence?.length > 0 && (
        <div className="px-5 pb-4" data-testid="hazard-precedence">
          <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: C.slate }}>How the flood band is decided</p>
          <ol className="space-y-1 list-decimal pl-4">
            {data.precedence.map((line, i) => <li key={i} className="text-[11px]" style={{ color: C.navy }}>{line}</li>)}
          </ol>
        </div>
      )}

      <div className="border-t border-[rgba(136,150,168,0.15)]">
        <table className="w-full table-fixed">
          <colgroup><col style={{ width: '44%' }} /><col style={{ width: '14%' }} /><col style={{ width: '30%' }} /><col style={{ width: '12%' }} /></colgroup>
          <thead>
            <tr style={{ background: C.bg }}>
              {['Layer', 'Jurisdiction', 'Coverage & licence', 'Health'].map(h => (
                <th key={h} className="px-4 py-2 text-[10px] uppercase tracking-wider font-semibold text-left" style={{ color: C.slate }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TIERS.map(t => {
              const rows = sources.filter(s => tierOf(s) === t.key);
              if (!rows.length) return null;
              return (
                <React.Fragment key={t.key}>
                  <tr style={{ background: 'rgba(26,76,124,0.05)' }} data-testid={`hazard-tier-${t.key}`}>
                    <td colSpan={4} className="px-4 py-1.5 text-[10px] uppercase tracking-wider font-semibold whitespace-normal" style={{ color: C.blue }}>
                      {t.label} <span className="normal-case tracking-normal font-normal" style={{ color: C.slate }}>— {t.note} · {rows.length}</span>
                    </td>
                  </tr>
                  {rows.map(s => <SourceRow key={s.code} s={s} verdict={verdicts[s.code]} />)}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {data.not_connected?.length > 0 && (
        <div className="px-5 py-4 border-t border-[rgba(136,150,168,0.15)]" data-testid="hazard-not-connected">
          <p className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: C.slate }}>Asked for, not connected — and why</p>
          {data.not_connected.map((n, i) => (
            <p key={i} className="text-[11px]" style={{ color: C.navy }}><span className="font-medium">{n.jurisdiction}:</span> <span style={{ color: C.slate }}>{n.reason}</span></p>
          ))}
        </div>
      )}

      <div className="p-5 pt-4 border-t border-[rgba(136,150,168,0.15)]">
        <p className="text-[10px] uppercase tracking-wider mb-2" style={{ color: C.slate }}>What a determination does</p>
        <div className="space-y-1.5">
          {(data.band_rules || []).map((r, i) => (
            <p key={i} className="text-[11px]" style={{ color: C.navy }} data-testid={`hazard-band-rule-${i}`}>
              {r.tier && <span className="font-mono text-[9px] uppercase mr-1.5" style={{ color: C.slate }}>{r.tier}</span>}
              <span className="font-medium">{r.determination}</span>
              <span className="mx-1.5 font-mono text-[10px] px-1 py-0.5 rounded" style={bandStyle(r.band)}>{r.band}</span>
              <span style={{ color: C.slate }}>{r.consequence}</span>
            </p>
          ))}
        </div>
      </div>
    </Card>
  );
};

export default HazardSourcesCard;
